import { emailRegex } from "@redotech/email/address";

export type Email = string;

/**
 * Un-validated email address and name type.
 */
export interface EmailAddressInfo {
  email?: Email | null;
  name?: string | null;
}

/**
 * Validated email addresses and optional name.
 */
export type EmailAddressInfoWithValidEmail = {
  email: Email;
} & EmailAddressInfo;

export type EmailSMTPMessageId = string;

/**
 * Email envelope info - an empty list means "don't send any cc / bcc / to", but undefined means "use the default by looking up from the conversation object"
 * If you pass lists to all 3, ensure at least one email is present in one of the lists.
 */
export interface EmailEnvelopeInfo {
  to?: EmailAddressInfoWithValidEmail[] | undefined;
  cc?: EmailAddressInfoWithValidEmail[] | undefined;
  bcc?: EmailAddressInfoWithValidEmail[] | undefined;
  from: EmailAddressInfoWithValidEmail;

  /**
   * Used for 2 things:
   * 1. for the inReplyTo header of this email
   * 2. to create the reply quote, by looking up the message
   * whose email's id matches this.
   *
   * If we make email reply threads editable at time of send, the entire
   * html including the reply will need to be passed here instead
   */
  inReplyTo?: EmailSMTPMessageId | undefined;

  /**
   * Used to uniquely identify the message that this draft is in reply to, if applicable.
   * Currently this only applies to public email drafts (internal notes and non-email platforms only support one draft per conversation).
   */
  inReplyToMongoId?: string | undefined;
}

export enum EmailReplyType {
  REPLY = "Reply",
  REPLY_ALL = "Reply All",
  FORWARD = "Forward",
}

type EmailRecipients = Omit<
  EmailEnvelopeInfo,
  "from" | "inReplyTo" | "inReplyToMongoId"
>;

function countNRecipientEmails(
  emailEnvelopeInfo: EmailRecipients | undefined,
): number {
  return (
    (emailEnvelopeInfo?.to?.length ?? 0) +
    (emailEnvelopeInfo?.cc?.length ?? 0) +
    (emailEnvelopeInfo?.bcc?.length ?? 0)
  );
}

export function hasExactlyOneRecipientEmail(
  emailEnvelopeInfo: EmailRecipients | undefined,
): boolean {
  return countNRecipientEmails(emailEnvelopeInfo) === 1;
}

/**
 * Returns the lowercased email if it matches the email regex, otherwise undefined.
 */
export const filterAndPreprocessOneEmail = (
  email: string | null | undefined,
): Email | undefined => {
  const match = email?.toLowerCase()?.match(emailRegex);
  return match?.[0];
};

/**
 * Ensure each email both is a valid email and is lowercased.
 */
export function filterAndPreprocessEmailInfo(
  emailAddressInfo: EmailAddressInfo[],
): EmailAddressInfoWithValidEmail[] {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return emailAddressInfo
    .map((emailAddressInfo) => ({
      ...emailAddressInfo,
      email: filterAndPreprocessOneEmail(emailAddressInfo?.email),
    }))
    .filter(
      (emailAddressInfo) => emailAddressInfo.email != null,
    ) as EmailAddressInfoWithValidEmail[];
}

export function stringifyEmailRecipientsInfo(
  emailEnvelopeInfo: EmailRecipients,
): string {
  const to = emailEnvelopeInfo.to?.map((e) => e.email).join(", ");
  const cc = emailEnvelopeInfo.cc?.map((e) => e.email).join(", ");
  const bcc = emailEnvelopeInfo.bcc?.map((e) => e.email).join(", ");
  const formattedTo = to ? `to: ${to}` : "";
  const formattedCc = cc ? `cc: ${cc}` : "";
  const formattedBcc = bcc ? `bcc: ${bcc}` : "";
  return [formattedTo, formattedCc, formattedBcc].filter(Boolean).join(", ");
}

export const tryGetDisplayNameOfEmailer = (
  emailAddressInfo: EmailAddressInfo,
): string | undefined => {
  return emailAddressInfo.name || emailAddressInfo?.email?.split("@")?.[0];
};
